import { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Search, ExternalLink, Plus, Upload, Pencil, Trash2, ChevronRight, ChevronDown, Merge, X } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { AssetMergeDialog } from './AssetMergeDialog';
import { toast } from 'sonner';
import useEquipmentCatalog from '../../hooks/useEquipmentCatalog';
import UnitFormDialog from './UnitFormDialog';
import UnitsImportDialog from './UnitsImportDialog';
import UnitHistoryPanel from './UnitHistoryPanel';
import { extractErrorMessage } from '../../utils/apiErrors';
import './CustomerUnitsTab.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cell = ({ children, mono }) => (
  <td className={`px-4 py-3 text-sm text-slate-700 ${mono ? 'font-mono' : ''}`}>{children || <span className="text-slate-400">—</span>}</td>
);

export default function CustomerUnitsTab({ units, customer, canManage, onChanged }) {
  const [q, setQ] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const selectedAssets = (units || []).filter((u) => selected.has(u.id));
  const toggle = (id) => setSelected((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const navigate = useNavigate();
  const { labelForType: typeLabel } = useEquipmentCatalog();

  const s = q.toLowerCase();
  const rows = (units || []).filter((u) => !s || ['unit_id', 'style', 'product_pumped', 'lsd', 'location', 'make', 'model', 'size', 'serial_number', 'equipment_type']
    .some((f) => (u[f] || '').toLowerCase().includes(s)) || (u.search_terms || []).some((term) => term.toLowerCase().includes(s)));

  const remove = async () => {
    try {
      await axios.delete(`${API}/customer-units/${deleting.manual_id}`);
      toast.success('Asset deleted');
      setDeleting(null);
      onChanged?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to delete asset'));
    }
  };

  return (
    <div className="space-y-4" data-testid="customer-units-tab">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search asset ID, style, product, LSD..." className="pl-9" data-testid="customer-units-search" />
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="customer-units-import-btn">
              <Upload size={14} className="mr-2" /> Import CSV
            </Button>
            <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="customer-units-add-btn">
              <Plus size={14} className="mr-2" /> Add Asset
            </Button>
          </div>
        )}
      </div>
      {canManage && <div className="flex flex-wrap gap-3 items-center" data-testid="customer-assets-merge-toolbar">
        <span className="text-sm text-slate-500" data-testid="customer-assets-selected-count">{selectedAssets.length} selected</span>
        <Button variant="outline" size="sm" disabled={selectedAssets.length < 2 || selectedAssets.length > 20} onClick={() => setMergeOpen(true)} data-testid="customer-assets-merge-button"><Merge size={14} className="mr-2" />Merge assets</Button>
        {!!selectedAssets.length && <Button variant="ghost" size="icon" aria-label="Clear asset selection" title="Clear selection" onClick={() => setSelected(new Set())} data-testid="customer-assets-clear-selection"><X size={14} /></Button>}
        {selectedAssets.length > 20 && <p className="text-sm text-amber-700" role="alert" data-testid="customer-assets-selection-limit">Select up to 20 assets per merge.</p>}
      </div>}
      <div className="min-w-0 bg-white border border-slate-200 rounded-lg shadow-sm">
        <table className="customer-units-table w-full" data-testid="customer-units-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {['', 'Asset ID', 'Type', 'Style', 'Make / Model', 'Serial #', 'Product Pumped', 'Site', 'LSD', 'Last Seen', ''].map((h, i) => (
                <th key={i} className="px-3 py-3 text-left text-xs font-bold uppercase text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {rows.map((u) => (
              <Fragment key={u.id}>
              <tr className={`hover:bg-slate-50 ${expanded === u.id || selected.has(u.id) ? 'bg-slate-50' : ''}`} data-testid={`customer-asset-row-${u.id}`}>
                <td className="pl-3 pr-1 py-3 text-slate-400">
                  <div className="flex gap-2 items-center">{canManage && <Checkbox checked={selected.has(u.id)} onCheckedChange={() => toggle(u.id)} aria-label={`Select asset ${u.unit_id || u.serial_number}`} data-testid={`customer-asset-select-${u.id}`} />}
                  <button type="button" onClick={() => setExpanded(expanded === u.id ? null : u.id)} aria-label="Asset history" aria-expanded={expanded === u.id} data-testid={`customer-asset-expand-${u.id}`}>{expanded === u.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button></div>
                </td>
                <Cell mono>{u.unit_id}</Cell>
                <Cell>{typeLabel(u.equipment_type)}</Cell>
                <Cell>{u.style}</Cell>
                <Cell><span data-testid={`unit-equipment-${encodeURIComponent(u.id)}`}>{[u.make, u.model, u.size && `Size ${u.size}`].filter(Boolean).join(' · ')}</span></Cell>
                <Cell mono>{u.serial_number}</Cell>
                <Cell>{u.product_pumped}</Cell>
                <Cell>{u.location}</Cell>
                <Cell mono>{u.lsd}</Cell>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  {u.last_job_id ? (
                    <button type="button" onClick={() => navigate(`/jobs/${u.last_job_id}`)} className="inline-flex items-center gap-1 text-sky-700 hover:underline" data-testid={`customer-asset-open-job-${u.id}`}>
                      {u.last_seen_on} <ExternalLink size={12} />
                    </button>
                  ) : u.last_seen_on}
                  {u.last_seen_at && <span className="block text-xs text-slate-400">{u.last_seen_at}{u.seen_count > 1 ? ` · ${u.seen_count} records` : ''}</span>}
                  {u.asset_id && <span className="block text-xs text-sky-700 mt-1" data-testid={`customer-asset-merged-${u.id}`}>Consolidated asset</span>}
                </td>
                <td className="px-2 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  {canManage && (u.manual_id || u.asset_id) && (
                    <>
                      <button type="button" onClick={() => { setEditing(u); setFormOpen(true); }} className="p-1.5 text-slate-400 hover:text-slate-900" aria-label="Edit asset" data-testid={`customer-asset-edit-${u.id}`}><Pencil size={14} /></button>
                      {!u.asset_id && <button type="button" onClick={() => setDeleting(u)} className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Delete asset" data-testid={`customer-asset-delete-${u.id}`}><Trash2 size={14} /></button>}
                    </>
                  )}
                  {canManage && !u.manual_id && !u.asset_id && (
                    <button type="button" onClick={() => { setEditing({ ...u, manual_id: null }); setFormOpen(true); }} className="text-xs text-sky-700 hover:underline px-1.5" title="Save this asset to the customer record so it can be edited" data-testid={`customer-asset-save-record-${u.id}`}>Save to record</button>
                  )}
                </td>
              </tr>
              {expanded === u.id && (
                <tr className="unit-history-row bg-slate-50/70" data-testid="customer-unit-history-row">
                  <td colSpan={11} className="p-0 border-t border-slate-100">
                    <UnitHistoryPanel customerId={customer.id} unit={u} />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="p-12 text-center text-slate-500 text-sm" data-testid="customer-units-empty">
            {q ? 'No assets match your search.' : 'No customer assets recorded yet.'}
          </div>
        )}
      </div>

      <UnitFormDialog open={formOpen} onOpenChange={setFormOpen} customer={customer} unit={editing} onSaved={onChanged} />
      <UnitsImportDialog open={importOpen} onOpenChange={setImportOpen} customer={customer} onImported={onChanged} />
      <AssetMergeDialog open={mergeOpen} onOpenChange={setMergeOpen} customer={customer} selected={selectedAssets} onMerged={async () => { setSelected(new Set()); setExpanded(null); await onChanged?.(); }} />
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete asset</AlertDialogTitle>
            <AlertDialogDescription>Remove <strong>{deleting?.unit_id || deleting?.serial_number}</strong> from this customer&apos;s record? Assets still referenced on jobs will keep showing from those jobs.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="customer-unit-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
